'use client';
import React, { useState } from 'react';

import {
  dateRangeMedWithZoneOffset,
  newsDateString,
} from '../../../helpers/dates';
import { IContentfulImage } from '../../../interfaces/contentful-image.interface';

import { PageComponentImageTags } from './page-component-image-tags';
import { ILibraryEventType } from '@/lib/fragments/libraryEventType';
import ImageWrapper from '@/components/image-wrapper/Image-wrapper';
import Image from 'next/image';

interface PageComponentImageProps {
  image?: IContentfulImage;
  imageURL?: string;
  imageAlt?: string;
  logo?: IContentfulImage;
  tags?: ILibraryEventType[];
  stickyImage?: boolean;
  header?: string;
  date?: string;
  isdatevisible?: boolean;
  eventDateFrom?: string;
  eventDateTo?: string;
  hasColoredBackground?: boolean;
  hasShadedOverlay?: boolean;
  node_locale: string;
}

export const PageComponentImage = ({
  image,
  imageURL,
  imageAlt,
  logo,
  tags,
  header,
  date,
  isdatevisible,
  eventDateFrom,
  eventDateTo,
  stickyImage = false,
  hasColoredBackground = false,
  hasShadedOverlay = false,
  node_locale,
}: PageComponentImageProps) => {
  const stickyWrapperClass = 'lg:h-full';

  return (
    <div
      className={`h-auto pb-0 lg:pb-11 ${
        stickyImage ? stickyWrapperClass : ''
      }`}
    >
      <div
        className={`relative transform-gpu overflow-hidden rounded-xl md:rounded-xxl ${
          stickyImage ? 'lg:sticky lg:top-0' : ''
        }`}
      >
        {hasColoredBackground ? ( // This is for service page.
          <div className="flex aspect-1.5/1 items-center justify-center overflow-hidden bg-brand-white lg:bg-background-primary-light">
            {imageURL ? (
              <div className="flex justify-center w-36">
                <Image
                  src={imageURL}
                  width={84}
                  height={84}
                  alt={imageAlt || 'alt text not found'}
                />
              </div>
            ) : (
              image?.url && (
                <ImageWrapper
                  url={image?.url}
                  alt={
                    imageAlt || image?.description
                      ? image.description
                      : 'alt text not found'
                  }
                  className="flex justify-center w-36"
                  width={image?.width ? parseInt(image.width) : undefined}
                  height={image?.height ? parseInt(image.height) : undefined}
                />
              )
            )}
            {logo?.url && (
              <ImageWrapper
                url={logo?.url}
                alt={
                  imageAlt || logo?.description
                    ? logo.description
                    : 'alt text not found'
                }
                className="!absolute left-6 top-6 h-16 w-16 lg:left-8 lg:top-8 lg:h-20 lg:w-20 rounded-md"
                width={logo?.width ? parseInt(logo.width) : undefined}
                height={logo?.height ? parseInt(logo.height) : undefined}
              />
            )}
          </div>
        ) : (
          <>
            {imageURL ? (
              <img
                src={imageURL + '?fm=webp&q=50'}
                alt={imageAlt || 'alt text not found'}
              />
            ) : (
              // <div className="relative h-screen">
              //   <Image
              //     src={imageURL}
              //     alt=""
              //     fill
              //     quality={60}
              //     priority
              //     sizes="(max-width: 768px) 100vw, (max-width: 1200px) 864px, 1200px"
              //     placeholder="blur"
              //     blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQEASABIAAD/2wBDABALDA4MChAODQ4SEhQVFhQYFhYaGBcYGBcaGhgYGBcaGhgYGBcaGhgYGBcaGhgYGBcaGhgYGBcaGhgYGBcaGv/AABEIAAEAAQMBIgACEQEDEQH/xAAXAAEBAQEAAAAAAAAAAAAAAAABAgAD/8QAFhABAQEAAAAAAAAAAAAAAAAAAQAC/9oADAMBAAIQAxAAAAG7AP/EABgQAQEBAQEAAAAAAAAAAAAAAAERACEx/9oACAEBAAEFAk0bK//EABYRAQEBAAAAAAAAAAAAAAAAAAEAEf/aAAgBAwEBPwGn/8QAFhEBAQEAAAAAAAAAAAAAAAAAABEB/9oACAECAQE/AYf/xAAbEAACAgMBAAAAAAAAAAAAAAABEQAhMUFhcf/aAAgBAQAGPwKxK1iZr//EABoQAQADAQEBAAAAAAAAAAAAAAERIQAxQWH/2gAIAQEAAT8hKzFhYk8Q7//aAAwDAQACAAMAAAAQ8//EABcRAQEBAQAAAAAAAAAAAAAAAAEREDH/2gAIAQMBAT8QkP/EABYRAQEBAAAAAAAAAAAAAAAAAAEQEf/aAAgBAgEBPxB//8QAGxABAAMAAwEAAAAAAAAAAAAAAREAITFBUWH/2gAIAQEAAT8QKQZVQ3Zl2cG7Hn//2Q=="
              //   />
              // </div>
              image?.url && (
                <ImageWrapper
                  url={image?.url}
                  alt={image?.description || undefined}
                  width={image?.width ? parseInt(image.width) : undefined}
                  height={image?.height ? parseInt(image.height) : undefined}
                />
              )
            )}
            {logo?.url && (
              <ImageWrapper
                url={logo?.url}
                alt={logo?.description ? logo.description : ''}
                className="!absolute left-6 top-6 h-16 w-16 bg-brand-white lg:left-8 lg:top-8 lg:h-20 lg:w-20"
                imgClassName="!object-contain"
                width={logo?.width ? parseInt(logo.width) : undefined}
                height={logo?.height ? parseInt(logo.height) : undefined}
              />
            )}
            {hasShadedOverlay && (
              <div className="absolute inset-0 bg-gradient-to-b from-transparency-gradient-from to-transparency-gradient-to"></div>
            )}
          </>
        )}

        <PageComponentImageTags
          tags={tags}
          endDate={eventDateTo}
          node_locale={node_locale}
        />

        {header && (date || eventDateTo) && (
          <>
            <div className="absolute bottom-6 left-6 text-brand-white lg:bottom-8 lg:left-8">
              {eventDateTo && eventDateFrom && (
                <div className="mb-1 font-title text-headings text-brand-yellow-1">
                  {dateRangeMedWithZoneOffset(
                    eventDateFrom,
                    eventDateTo,
                    node_locale,
                  )}
                </div>
              )}
              <div className="hidden font-title text-headingm lg:block lg:pr-8">
                {header}
              </div>
              {(isdatevisible == null || isdatevisible == true) && date && (
                <div className="mt-4">{newsDateString(date, node_locale)}</div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
